import * as ActionTypes from "../actions/types";
import v4 from "uuid";
import { mapFileUploadErrors } from "../constants/map/organization";

const INITIAL_STATE = {
  form: {},
  enableReinitialize: false,
  keepDirtyOnReinitialize: false,
  fileUploads: {},
  uploadingFile: false,
  organizationsToCreate: {},
  organizationsToUpdate: {},
  errors: {},
};

function prepareOrganizationForm(state, action) {
  state.form = action.payload;
  state.enableReinitialize = true;
  state.keepDirtyOnReinitialize = true;
  return state;
}

function updateOrganizationFormField(state, action) {
  if (!state.form) {
    state.form = {};
  }
  state.form[action.payload.name] = action.payload.value;
  return state;
}

function clearOrganizationForm() {
  return { ...INITIAL_STATE };
}

function uploadFile(state, action) {
  const { uuid } = action.payload;
  state.uploadingFile = uuid;
  return state;
}

function uploadFileSuccess(state, action) {
  const { id } = action.payload.data;
  const fileUploaded = state.uploadingFile;
  const fileData = state.fileUploads?.[fileUploaded] || {};

  const { organizationUuid, type } = fileData;
  const organizationFromState =
    state.organizationsToUpdate?.[organizationUuid] || {};

  const organization = {
    ...organizationFromState,
    [type]: [
      ...(organizationFromState[type] ? organizationFromState[type] : {}),
      id,
    ],
    waitFor: organizationFromState.waitFor - 1,
    status: organizationFromState.waitFor - 1 === 0 ? 1 : 0,
  };

  state.uploadingFile = false;
  if (!state.organizationsToUpdate) state.organizationsToUpdate = {};
  state.organizationsToUpdate[organizationUuid] = organization;
  if (state.fileUploads && state.fileUploads[fileUploaded]) {
    delete state.fileUploads[fileUploaded];
  }
  return state;
}

function uploadFileFailed(state, action) {
  const fileUploaded = state.uploadingFile;
  const fileData = state.fileUploads?.[fileUploaded] || {};
  const { organizationUuid, fileName } = fileData;

  const organizationFromState =
    state.organizationsToUpdate?.[organizationUuid] || {};
  const organization = {
    ...organizationFromState,
    status: organizationFromState.status + 1,
  };
  if (organization.status >= organization.waitFor) {
    organization.status = 1;
  }
  const errors = mapFileUploadErrors(action.payload, fileName);

  state.uploadingFile = false;
  if (!state.organizationsToUpdate) state.organizationsToUpdate = {};
  state.organizationsToUpdate[organizationUuid] = organization;
  if (state.fileUploads && state.fileUploads[fileUploaded]) {
    delete state.fileUploads[fileUploaded];
  }
  if (!state.errors) state.errors = {};
  if (!state.errors[organizationUuid]) state.errors[organizationUuid] = {};
  Object.assign(state.errors[organizationUuid], errors);
  return state;
}

function removeOrganizationFileUploadError(state, action) {
  const { organizationId, uuid } = action.payload;
  if (!state.errors) {
    return state;
  }

  if (typeof uuid === "undefined") {
    // Remove entire organization's errors
    delete state.errors[organizationId];
  } else {
    // Remove specific error by UUID
    if (state.errors[organizationId]) {
      delete state.errors[organizationId][uuid];
      // If no more errors for this organization, remove the organization key
      if (Object.keys(state.errors[organizationId]).length === 0) {
        delete state.errors[organizationId];
      }
    }
  }
  return state;
}

function updateOrganization(state, action) {
  const {
    uuid,
    title,
    description,
    image,
    documents,
    uploadedFiles,
    field_allowed_applications,
  } = action.payload;

  const createNewFileToUpload = (file, type) => ({
    uuid: v4(),
    file,
    organizationUuid: uuid,
    type,
    fileName: file.name,
  });

  const updateData = {
    uuid,
    title,
    description,
    image,
    uploadedFiles,
    field_allowed_applications,
  };

  const files = [];
  // Handle image upload
  if (image && image.length > 0) {
    for (let i = 0; i < image.length; i++) {
      files.push(createNewFileToUpload(image[i], "field_image"));
    }
  }

  // Handle documents upload
  if (documents && documents.length > 0) {
    for (let i = 0; i < documents.length; i++) {
      files.push(createNewFileToUpload(documents[i], "field_documents"));
    }
  }

  const newFileUploads = files.reduce(
    (previous, file) => ({
      ...previous,
      [file.uuid]: file,
    }),
    {}
  );

  if (files.length > 0) {
    if (!state.fileUploads) state.fileUploads = {};
    Object.assign(state.fileUploads, newFileUploads);
    if (!state.organizationsToUpdate) state.organizationsToUpdate = {};
    state.organizationsToUpdate[uuid] = {
      ...updateData,
      status: 0,
      waitFor: files.length,
    };
  } else {
    if (!state.organizationsToUpdate) state.organizationsToUpdate = {};
    state.organizationsToUpdate[uuid] = {
      ...updateData,
      status: 1,
    };
  }
  return state;
}

function updateOrganizationSuccess(state, action) {
  const { data } = action.payload;
  if (state.organizationsToUpdate && state.organizationsToUpdate[data.id]) {
    delete state.organizationsToUpdate[data.id];
  }
  return state;
}

function updateOrganizationFailed(state) {
  state.loading = false;
  return state;
}

function createOrganization(state) {
  state.loading = true;
  return state;
}

function createOrganizationSuccess(state) {
  state.loading = false;
  return state;
}

function createOrganizationFailed(state, action) {
  state.loading = false;

  // Store the error for display in the form
  if (!state.errors) state.errors = {};
  state.errors.createOrganization = action.payload;

  return state;
}

export const organizationFormReducer = {
  initialState: INITIAL_STATE,
  handlers: {
    [ActionTypes.CLEAR_ORGANIZATION_FORM]: clearOrganizationForm,
    [ActionTypes.REMOVE_ORGANIZATION_FILE_UPLOAD_ERROR]:
      removeOrganizationFileUploadError,
    [ActionTypes.PREPARE_ORGANIZATION_FORM]: prepareOrganizationForm,
    [ActionTypes.UPDATE_ORGANIZATION_FORM_FIELD]: updateOrganizationFormField,

    [ActionTypes.UPDATE_ORGANIZATION]: updateOrganization,
    [ActionTypes.UPDATE_ORGANIZATION_FAILED]: updateOrganizationFailed,
    [ActionTypes.UPDATE_ORGANIZATION_SUCCESS]: updateOrganizationSuccess,

    [ActionTypes.UPLOAD_ORGANIZATION_FILE]: uploadFile,
    [ActionTypes.UPLOAD_ORGANIZATION_FILE_FAILED]: uploadFileFailed,
    [ActionTypes.UPLOAD_ORGANIZATION_FILE_SUCCESS]: uploadFileSuccess,

    [ActionTypes.CREATE_ORGANIZATION]: createOrganization,
    [ActionTypes.CREATE_ORGANIZATION_FAILED]: createOrganizationFailed,
    [ActionTypes.CREATE_ORGANIZATION_SUCCESS]: createOrganizationSuccess,
  },
};
